<template>
    <section class="sms-broadcast-preview">
        <span class="preview-title">{{ previewLabel }}</span>

        <div class="preview-wrapper">
            <div class="phone">
                <DsTextButton
                    dense
                    class="opt-in-toggle-button"
                    :disabled="isOptInAnimationRunning"
                    @click="handleOptInButtonClick"
                >
                    {{ isHideButtonShown || isOptInAnimationRunning ? previewHideDoubleOpt : previewShowDoubleOpt }}
                </DsTextButton>

                <div class="messages-wrapper">
                    <transition name="fade" mode="out-in">
                        <div
                            v-if="!isOptInMessageShown"
                            key="standard"
                            ref="messages"
                            class="messages"
                        >
                            <span class="fill-space" />

                            <span class="message message-preview">
                                {{ messagePreview }}
                            </span>

                            <div class="message-meta-data">
                                {{ formattedPhoneNumber }} {{ justNowMsg }}
                            </div>
                        </div>

                        <div
                            v-else
                            key="opt-in"
                            ref="messages"
                            class="messages"
                            :class="{ 'scrolling-disabled': !isMessageShown }"
                        >
                            <div class="opt-in-wrapper">
                                <div v-if="isOptInShown" class="opt-in-message-wrapper">
                                    <div class="message opt-in-message" @mouseover="isOptInMessageHovered = true" @mouseleave="isOptInMessageHovered = false">
                                        <button class="message-button" @click="isOptInMessageHovered = !isOptInMessageHovered">
                                            {{ optInMessage }}
                                        </button>

                                        <transition name="fade">
                                            <div v-if="isOptInMessageHovered" class="edit-overlay" @click="$emit('show-settings')">
                                                <DsFilledButton>
                                                    <template #leading>
                                                        <DsIcon name="edit" />
                                                    </template>

                                                    <template #default>
                                                        <span>Edit</span>
                                                    </template>
                                                </dsFilledButton>
                                            </div>
                                        </transition>
                                    </div>

                                    <div class="message-meta-data">
                                        {{ formattedPhoneNumber }} {{ timeAmText2 }}
                                    </div>
                                </div>

                                <div v-if="isReplyShown" class="opt-in-response-wrapper">
                                    <div class="message opt-in-response">
                                        {{ yesText }}
                                    </div>

                                    <div class="message-meta-data">
                                        {{ timeAmText1 }}
                                    </div>
                                </div>

                                <div v-if="isOptInConfirmationShown" class="message-wrapper">
                                    <div
                                        class="message opt-in-confirmation-message"
                                        @mouseover="isOptInConfirmationHovered = true"
                                        @mouseleave="isOptInConfirmationHovered = false"
                                    >
                                        <button class="message-button" @click="isOptInConfirmationHovered = !isOptInConfirmationHovered">
                                            {{ account.optInConfirmationMessage }}
                                        </button>

                                        <transition name="fade">
                                            <div v-if="isOptInConfirmationHovered" class="edit-overlay">
                                                <span class="confirmation-message-description">{{ previewWarnMsg }}</span>
                                            </div>
                                        </transition>
                                    </div>

                                    <div v-if="!isMessageShown" class="message-meta-data">
                                        {{ formattedPhoneNumber }} {{ justNowMsg }}
                                    </div>
                                </div>
                                <div v-if="isMessageShown" ref="message" class="message-wrapper">
                                    <div class="message">
                                        {{ messagePreview }}
                                    </div>

                                    <div class="message-meta-data">
                                        {{ formattedPhoneNumber }} {{ justNowMsg }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <div class="input-wrapper">
                        <div class="preview-input">
                            <DsIcon class="send-icon" name="send" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import debounce from 'lodash.debounce';

const formatPhone = (phone) => {
    if (!phone) {
        return null;
    }

    let numbers = phone.replace(/\D/g, '');

    if (numbers.startsWith('1')) {
        numbers = numbers.substring(1);
    }

    const char = { 0: '(', 3: ') ', 6: '-' };

    phone = '';

    for (let i = 0; i < numbers.length; i++) {
        phone += (char[i] || '') + numbers[i];
    }

    return phone;
};

export const TIME_FORMAT = 'h:mm a';
export const DISPLAY_DATE_FORMAT_WITH_YEAR = 'MMM D, YYYY';

const SCROLL_TO_MESSAGES_BOTTOM_DELAY = 300;
const OPT_IN_DELAY = 300;
const MESSAGES_DELAY = 1000;
const REPLY_DELAY = OPT_IN_DELAY + MESSAGES_DELAY;
const OPT_IN_CONFIRMATION_DELAY = REPLY_DELAY + MESSAGES_DELAY;
const MESSAGE_DELAY = OPT_IN_CONFIRMATION_DELAY + MESSAGES_DELAY;
const HIDE_BUTTON_DELAY = MESSAGE_DELAY + MESSAGES_DELAY;

export default {
    props: {
        account: {
            type: Object,
            default: () => ({}),
        },
        message: String,
    },

    data() {
        return {
            isOptInMessageShown: false,
            isOptInMessageHovered: false,
            isOptInShown: false,
            isReplyShown: false,
            isOptInConfirmationShown: false,
            isOptInConfirmationHovered: false,
            isMessageShown: false,
            isHideButtonShown: false,
            isOptInAnimationRunning: false,
            timeouts: {
                optInTimeout: null,
                replyTimeout: null,
                optInConfirmationTimeout: null,
                messageTimeout: null,
                hideButtonTimeout: null,
            },
            previewLabel: '',
            previewShowButton: '',
            previewHideButton: '',
            previewShowDoubleOpt: '',
            previewHideDoubleOpt: '',
            timeAmText1: '',
            timeAmText2: '',
            yesText: '',
            previewText: '',
            previewWarnMsg: '',
            justNowMsg: '',
        };
    },

    beforeDestroy() {
        this.clearTimeouts();
    },

    created() {
        this.scrollToMessagesBottomDebounced = debounce(this.scrollToMessagesBottom, SCROLL_TO_MESSAGES_BOTTOM_DELAY);

        if (window.keapI18nMap !== undefined) {
            this.previewLabel = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.label'];
            this.previewShowButton = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.show.button'];
            this.previewHideButton = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.hide.button'];
            this.previewShowDoubleOpt = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.show.doubleopt'];
            this.previewHideDoubleOpt = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.hide.doubleopt'];
            this.timeAmText1 = window.keapI18nMap['funnel.campaign.communications.textmsg.time.am.text1'];
            this.timeAmText2 = window.keapI18nMap['funnel.campaign.communications.textmsg.time.am.text2'];
            this.yesText = window.keapI18nMap['application.yes'];
            this.previewText = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.msg'];
            this.previewWarnMsg = window.keapI18nMap['funnel.campaign.communications.textmsg.preview.warnmsg'];
            this.justNowMsg = window.keapI18nMap['funnel.campaign.communications.textmsg.justnow.text'];
        } else {
            this.previewLabel = 'Preview';
            this.previewShowButton = 'Show opt-in message';
            this.previewHideButton = 'Hide opt-in message';
            this.previewShowDoubleOpt = 'Show double opt-in message';
            this.previewHideDoubleOpt = 'Hide double opt-in message';
            this.timeAmText1 = '9:37am';
            this.timeAmText2 = '9:36am';
            this.yesText = 'Yes';
            this.previewText = 'Enter message to see preview...';
            this.previewWarnMsg = 'Due to text marketing laws, this content can’t be edited.';
            this.justNowMsg = 'Just now';
        }
    },

    watch: {
        message() {
            this.scrollToMessagesBottomDebounced();
        },
    },

    computed: {
        formattedPhoneNumber() {
            return formatPhone(this.account?.phoneNumber);
        },

        messagePreview() {
            return this.message || this.previewText;
        },

        optInMessage() {
            return `${this.account.optInMessageIntro}${this.account.optInMessage}`;
        },
    },

    methods: {
        reset() {
            this.clearTimeouts();
            this.isOptInShown = false;
            this.isReplyShown = false;
            this.isOptInConfirmationShown = false;
            this.isMessageShown = false;
            this.isHideButtonShown = false;
            this.scrollToMessagesBottomDebounced();
        },

        clearTimeouts() {
            Object.keys(this.timeouts).forEach((timeout) => {
                clearTimeout(this.timeouts[timeout]);
            });
        },

        scrollToMessagesBottom() {
            if (this.$refs.messages) {
                this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
            }
        },

        scrollToMessagesBottomDebounced() {
            this.scrollToMessagesBottom();
        },

        showOptInMessage() {
            this.isOptInMessageShown = true;
            this.isOptInAnimationRunning = true;

            this.timeouts.optInTimeout = setTimeout(async () => {
                this.isOptInShown = true;

                await this.$nextTick();

                this.scrollToMessagesBottom();
            }, OPT_IN_DELAY);

            this.timeouts.replyTimeout = setTimeout(() => {
                this.isReplyShown = true;
                this.scrollToMessagesBottomDebounced();
            }, REPLY_DELAY);

            this.timeouts.optInConfirmationTimeout = setTimeout(async () => {
                this.isOptInConfirmationShown = true;

                await this.$nextTick();

                this.scrollToMessagesBottom();
            }, OPT_IN_CONFIRMATION_DELAY);

            this.timeouts.messageTimeout = setTimeout(async () => {
                this.isMessageShown = true;

                await this.$nextTick();

                this.scrollToMessagesBottom();
            }, MESSAGE_DELAY);

            this.timeouts.hideButtonTimeout = setTimeout(() => {
                this.isHideButtonShown = true;
                this.isOptInAnimationRunning = false;
            }, HIDE_BUTTON_DELAY);
        },

        hideOptInMessage() {
            this.isOptInMessageShown = false;

            this.reset();
        },

        handleOptInButtonClick() {
            if (this.isOptInMessageShown) {
                this.hideOptInMessage();
            } else {
                this.showOptInMessage();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    $phone-height-px: 579;
    $phone-width-px: 287;
    $phone-scrollbar-size-px: 10;
    $sms-input-height-px: 32;
    $sms-message-width-pct: 90;

    @mixin overflowY {
        overflow-y: auto;

        @supports (-webkit-overflow-scrolling: touch) {
            overflow-y: scroll;
            -webkit-overflow-scrolling: touch;
        }
    }

    .sms-broadcast-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: $color-gray-050;
        border-left: 1px solid $color-ink-200;
        @include overflowY;

        @media($small) {
            display: none;
        }
    }

    .preview-wrapper {
        display: inline-flex;
        flex-direction: column;
    }

    .preview-title {
        align-self: flex-start;
        font-size: px-to-rem(18);
        font-weight: $font-weight-semibold;
        color: $color-text-subtle;
        padding: .75rem .75rem 2.5rem;
    }
    .phone {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: px-to-rem($phone-width-px);
        height: px-to-rem($phone-height-px);
        background-color: $color-paper;
        border: 3px solid $color-ink;
        border-radius: $border-radius * 2;
        box-shadow: $elevation-z8;
    }

    .opt-in-toggle-button {
        align-self: center;
        margin-top: $spacing-100;
    }

    .messages-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        justify-content: space-between;
        width: 100%;
        overflow: hidden;
    }

    .messages {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        margin: $spacing-100 $spacing-050 $spacing-100 $spacing-200;
        padding-right: $spacing-150;
        overflow-x: hidden;
        @include overflowY;
        scroll-behavior: smooth;

        &::-webkit-scrollbar {
            width: px-to-rem($phone-scrollbar-size-px);
            height: px-to-rem($phone-scrollbar-size-px);
        }

        &::-webkit-scrollbar-thumb {
            background-color: $color-gray-500;
            border-radius: 3px;

            &:hover {
                background: $color-gray-700;
            }
        }

        &.scrolling-disabled {
            overflow-y: hidden;
            .opt-in-response-wrapper {
                margin-right: $spacing-100;
            }
        }
    }

    .date {
        margin-top: auto;
        padding-bottom: $spacing-100;
        align-self: center;
        color: $color-ink-700;
        font-size: $font-size-xs;
    }

    .message {
        word-break: break-word;
        white-space: pre-line;
        background-color: $color-ink-100;
        font-size: $font-size-md;
        line-height: $line-height-lg;
        max-width: ($sms-message-width-pct * 1%);
        width: 100%;
        padding: $spacing-200;
        border-radius: $border-radius;
    }

    .message-button {
        background-color: transparent;
        padding: 0;
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
        text-align: inherit;
    }

    .input-wrapper {
        padding: 0 $spacing-200 $spacing-200;
        width: 100%;
    }

    .preview-input {
        padding: 0 $spacing-075;
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
        height: px-to-rem($sms-input-height-px);
        width: 100%;
        background-color: $color-gray-050;
        border-radius: $border-radius * 0.5;
        --icon-color: #{$color-ink-200};
        --icon-size: #{px-to-rem(18.33)};
    }

    .opt-in-message {
        position: relative;
        overflow: hidden;

        .edit-overlay {
            cursor: pointer;
        }
    }

    .edit-overlay {
        position: absolute;
        background-color: $color-ink-500;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .opt-in-wrapper {
        display: flex;
        flex-direction: column;
    }

    .message-meta-data {
        padding: $spacing-100 0 $spacing-200;
        color: $color-text-subtle;
    }

    .opt-in-confirmation-message {
        position: relative;
        overflow: hidden;
    }
    .confirmation-message-description {
        background-color: $color-paper;
        width: 80%;
        padding: $spacing-100;
        font-size: $font-size-sm;
        border-radius: $border-radius * 0.5;
    }

    .opt-in-response-wrapper {
        display: flex;
        flex-direction: column;

        .opt-in-response {
            width: auto;
            align-self: flex-end;
            background-color: $color-green-050;
        }

        .message-meta-data {
            align-self: flex-end;
        }
    }

    .message-wrapper {
        margin-top: $spacing-100;
    }

    @keyframes fromBottomLeft {
        0% {
            transform: translate(-100%, 75%);
            opacity: 0;
            visibility: hidden;
        }

        80% {
            transform: translate(-10%, 0);
            opacity: 1;
            visibility: visible;
        }

        100% {
            transform: translate(0, 0);
        }
    }

    @keyframes fromBottomRight {
        0% {
            transform: translate(100%, 75%);
            opacity: 0;
            visibility: hidden;
        }

        80% {
            transform: translate(10%, 0);
            opactiy: 1;
            visibility: visible;
        }

        100% {
            transform: translate(0, 0);
        }
    }

    .opt-in-message-wrapper {
        animation: fromBottomLeft .3s ease-in-out 0s;
    }

    .opt-in-response-wrapper {
        animation: fromBottomRight .5s ease-in-out 0s;
    }

    .opt-in-confirmation-message {
        animation: fromBottomLeft .4s ease-in-out 0s;
    }

    .message-wrapper {
        animation: fromBottomLeft .4s ease-in-out 0s;
    }

    .fill-space {
        flex: 1 1 auto;
    }
</style>
